import React from "react";
import useReveal from "../../../lib/useReveal";
import "./case.scss";

// Content primitives for the case studies. These replace the fixed 1920x1080
// artboard coordinates: every block reflows, so a case study is readable at
// any width instead of being a shrunken board.

export const Section = ({ id, eyebrow, title, lead, children, tone }) => {
  const ref = useReveal({ threshold: 0.08 });
  return (
    <section
      className={`cs-section ${tone ? `cs-section--${tone}` : ""}`}
      id={id}
      ref={ref}
      aria-labelledby={id ? `${id}-h` : undefined}
    >
      {(title || lead) && (
        <header className="cs-head" data-reveal>
          {eyebrow && <p className="u-mono cs-eyebrow">{eyebrow}</p>}
          {title && (
            <h2 className="cs-h2" id={id ? `${id}-h` : undefined}>
              {title}
            </h2>
          )}
          {lead && <p className="cs-lead">{lead}</p>}
        </header>
      )}
      {children}
    </section>
  );
};

export const Prose = ({ children }) => (
  <div className="cs-prose" data-reveal>
    {children}
  </div>
);

export const Figure = ({ src, alt, caption, bleed, narrow, max, frame = true }) => (
  <figure
    className={`cs-figure ${bleed ? "cs-figure--bleed" : ""} ${
      narrow ? "cs-figure--narrow" : ""
    } ${frame ? "" : "cs-figure--bare"}`}
    style={max ? { maxWidth: max } : undefined}
    data-reveal
  >
    <img src={src} alt={alt} loading="lazy" decoding="async" />
    {caption && <figcaption>{caption}</figcaption>}
  </figure>
);

// Media beside body copy. Collapses to a single column below 900px, with the
// media first so the reader always sees the object before the explanation.
export const Split = ({ media, children, reverse, align = "center" }) => (
  <div
    className={`cs-split ${reverse ? "cs-split--reverse" : ""}`}
    style={{ alignItems: align }}
    data-reveal
  >
    <div className="cs-split__media">{media}</div>
    <div className="cs-split__body">{children}</div>
  </div>
);

// An image with labelled parts and leader lines. Everything is expressed as a
// percentage of the original artboard frame, so the labels, the rules and the
// artwork stay in register at any width. `media` places the render inside that
// frame; `lines` are drawn in the frame's own coordinate space.
export const Annotated = ({
  src,
  alt,
  points = [],
  lines = [],
  frame = { w: 1920, h: 1080 },
  media,
  caption,
  // Artwork on a light ground needs dark leader lines and vice versa.
  tone = "light",
}) => (
  <figure className={`cs-annotated cs-annotated--on-${tone}`} data-reveal>
    <div
      className="cs-annotated__stage"
      style={{ aspectRatio: `${frame.w} / ${frame.h}` }}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        style={
          media
            ? {
                position: "absolute",
                left: `${(media.l / frame.w) * 100}%`,
                top: `${(media.t / frame.h) * 100}%`,
                width: `${(media.w / frame.w) * 100}%`,
                height: `${(media.h / frame.h) * 100}%`,
              }
            : undefined
        }
      />

      {lines.length > 0 && (
        <svg
          className="cs-leaders"
          viewBox={`0 0 ${frame.w} ${frame.h}`}
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {lines.map((seg, i) => (
            <line
              key={i}
              x1={seg[0]}
              y1={seg[1]}
              x2={seg[2]}
              y2={seg[3]}
              pathLength="1"
              style={{ "--i": i }}
            />
          ))}
        </svg>
      )}

      {points.map((p, i) => (
        <span
          key={p.label}
          className={`cs-point cs-point--${p.side || "right"}`}
          style={{ left: `${p.x}%`, top: `${p.y}%`, "--i": i }}
        >
          <span className="cs-point__dot" aria-hidden="true">
            {i + 1}
          </span>
          <span className="cs-point__label">{p.label}</span>
        </span>
      ))}
    </div>
    <ol className="cs-annotated__key">
      {points.map((p) => (
        <li key={p.label}>{p.label}</li>
      ))}
    </ol>
    {caption && <figcaption>{caption}</figcaption>}
  </figure>
);

export const Specs = ({ items, columns = 2 }) => (
  <ul className={`cs-specs cs-specs--${columns}`} data-reveal>
    {items.map((item) => (
      <li key={typeof item === "string" ? item : item.label}>
        {typeof item === "string" ? (
          <span className="cs-specs__label">{item}</span>
        ) : (
          <>
            <span className="cs-specs__label">{item.label}</span>
            {item.body && <span className="cs-specs__body">{item.body}</span>}
          </>
        )}
      </li>
    ))}
  </ul>
);

export const Grid = ({ children, min = 260 }) => (
  <div
    className="cs-grid"
    style={{ "--min": `${min}px` }}
    data-reveal
  >
    {children}
  </div>
);

// ---- Immersive blocks --------------------------------------------------

// Full-bleed moment. The media outsizes its frame and drifts against the
// scroll, so the product fills the screen instead of sitting in a card.
export const Stage = ({
  src,
  alt,
  title,
  kicker,
  children,
  height = "100dvh",
  contain,
  align = "end",
}) => {
  const ref = useReveal({ threshold: 0.02 });
  const hasCopy = Boolean(title || kicker || children);
  return (
    <section
      className={`cs-stage cs-stage--${align} ${contain ? "cs-stage--contain" : ""} ${
        hasCopy ? "" : "cs-stage--bare"
      }`}
      style={{ "--stage-h": height }}
      ref={ref}
    >
      <div className="cs-stage__media" data-parallax>
        <img src={src} alt={alt} loading="lazy" decoding="async" />
      </div>
      {hasCopy && (
        <div className="cs-stage__copy">
          {kicker && <p className="u-mono cs-eyebrow">{kicker}</p>}
          {title && <h2 className="cs-stage__title">{title}</h2>}
          {children}
        </div>
      )}
    </section>
  );
};

// Big editorial type as its own beat between sections.
export const Statement = ({ children, offset }) => {
  const ref = useReveal({ threshold: 0.25 });
  return (
    <section className={`cs-statement ${offset ? "cs-statement--offset" : ""}`} ref={ref}>
      <p data-reveal>{children}</p>
    </section>
  );
};

// Scrollytelling: the media holds while the copy moves past it. CSS sticky
// rather than a ScrollTrigger pin, so it degrades to a plain stack when
// sticky is unavailable and costs nothing on the main thread.
export const Sticky = ({ media, children, reverse }) => {
  const ref = useReveal({ threshold: 0.05 });
  return (
    <section
      className={`cs-sticky ${reverse ? "cs-sticky--reverse" : ""}`}
      ref={ref}
    >
      <div className="cs-sticky__media">
        <div className="cs-sticky__inner">{media}</div>
      </div>
      <div className="cs-sticky__body">{children}</div>
    </section>
  );
};

// A slide-style frame: a narrow column of copy against a composed device
// stage. The three renders stay separate files rather than one flattened
// image so each stays crisp and the arrangement can reflow.
export const Devices = ({ monitor, phones = [], children }) => {
  const ref = useReveal({ threshold: 0.05 });
  return (
    <section className="cs-devices" ref={ref}>
      <div className="cs-devices__copy">{children}</div>
      <div className="cs-devices__stage">
        <img
          className="cs-devices__monitor"
          src={monitor.src}
          alt={monitor.alt}
          loading="lazy"
          decoding="async"
        />
        {phones.map((phone, i) => (
          <img
            key={phone.src}
            className={`cs-devices__phone cs-devices__phone--${i + 1}`}
            src={phone.src}
            alt={phone.alt}
            loading="lazy"
            decoding="async"
          />
        ))}
      </div>
    </section>
  );
};

export const Beat = ({ title, children }) => (
  <div className="cs-beat" data-reveal>
    {title && <h3 className="cs-h3">{title}</h3>}
    {children}
  </div>
);
